import React, { useEffect, useState } from 'react';
import './mcp.css';
import { message, Spin } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, LinkOutlined, DisconnectOutlined, SyncOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { authHeader } from '../../../utils/api';
import axios from 'axios';

const CRM_PLATFORMS = [
  { key: 'salesforce', name: 'Salesforce', logo: '/crm_logos/salesforrce.png', color: '#00A1E0', description: 'Connect to Salesforce CRM to sync contacts and campaigns' },
  { key: 'housecall', name: 'Housecall Pro', logo: '/crm_logos/Housecall.png', color: '#FF6B35', description: 'Connect to Housecall Pro for field service management' },
  { key: 'servicetitan', name: 'ServiceTitan', logo: '/crm_logos/ServiceTitan.png', color: '#FF6B35', description: 'Connect to ServiceTitan for service business automation' },
  { key: 'fieldedge', name: 'FieldEdge', logo: '/crm_logos/FieldEdge.png', color: '#2E7D32', description: 'Connect to FieldEdge for field service management' },
  { key: 'zoho', name: 'Zoho', logo: '/crm_logos/Zoho.png', color: '#3371E5', description: 'Connect to Zoho CRM for comprehensive business management' },
  { key: 'hubspot', name: 'HubSpot', logo: '/crm_logos/hubspot.png', color: '#FF7A59', description: 'Connect to HubSpot for inbound marketing and sales' },
  { key: 'gohighlevel', name: 'GoHighLevel', logo: '/crm_logos/GoHighLevel.png', color: '#1E5FD1', description: 'Connect to GoHighLevel for marketing automation' },
  { key: 'nutshell', name: 'Nutshell', logo: '/crm_logos/Nutshell.png', color: '#6B46C1', description: 'Connect to Nutshell for simple CRM solution' },
  { key: 'servicefusion', name: 'Service Fusion', logo: '/crm_logos/ServiceFusion.png', color: '#FF6B35', description: 'Connect to Service Fusion for field service management' },
  { key: 'kickserv', name: 'Kickserv', logo: '/crm_logos/Kickserv.jpg', color: '#2E7D32', description: 'Connect to Kickserv for field service software' },
  { key: 'successware', name: 'Successware', logo: '/crm_logos/SuccessWare.png', color: '#FF6B35', description: 'Connect to Successware for service business management' },
];

function StatusPill({ connected }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: connected ? 'var(--sage)' : 'var(--ink-200)' }}>
      {connected
        ? <><CheckCircleFilled /> Connected</>
        : <><CloseCircleFilled /> Not connected</>}
    </div>
  );
}

function CrmCard({ platform, isConnected, connection, onConnect, onSync, onDisconnect, loading }) {
  const logoUrl = platform.logo || null;
  return (
    <div className="mcp-card3d" style={{ padding: 24, display: 'flex', flexDirection: 'column', borderColor: isConnected ? platform.color : 'var(--paper-300)', borderWidth: isConnected ? 2 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14, flexShrink: 0,
          background: isConnected ? 'var(--ink)' : 'var(--paper-300)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>
          {logoUrl ? (
            <img src={logoUrl} alt={platform.name} style={{ width: 32, height: 32, objectFit: 'contain' }} />
          ) : (
            <span style={{ color: isConnected ? '#fff' : 'var(--ink-300)', fontWeight: 800 }}>{platform.name.charAt(0)}</span>
          )}
        </div>
        <StatusPill connected={isConnected} />
      </div>

      <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--ink)', marginBottom: 2, letterSpacing: '-0.02em' }}>{platform.name}</div>
      <p style={{ fontSize: 13, color: 'var(--ink-100)', lineHeight: 1.55, margin: '0 0 16px' }}>{platform.description}</p>

      {isConnected && connection?.connectedAt && (
        <div className="mcp-eyebrow" style={{ marginBottom: 12 }}>
          Connected: {new Date(connection.connectedAt).toLocaleDateString()}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        {!isConnected ? (
          <button
            type="button"
            onClick={onConnect}
            disabled={loading}
            style={{
              flex: 1,
              borderRadius: 9999,
              background: platform.color,
              border: 'none',
              color: '#fff',
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <LinkOutlined /> Connect
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onSync}
              disabled={loading}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-paper-300 bg-paper px-3 py-2 text-xs font-semibold text-ink transition hover:border-ink/30 disabled:opacity-60"
            >
              <SyncOutlined spin={loading} /> Sync clients
            </button>
            <button
              type="button"
              onClick={onDisconnect}
              disabled={loading}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-paper-300 bg-paper px-3 py-2 text-xs font-semibold text-ember transition hover:border-ember/40 disabled:opacity-60"
            >
              <DisconnectOutlined /> Disconnect
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function McpCRMAuth() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [crmConnections, setCrmConnections] = useState({});
  const [lastSyncTime, setLastSyncTime] = useState(null);

  useEffect(() => {
    const loadAndSyncCrmConnections = async () => {
      try {
        const response = await axios.get('/api/clients/me/crm-connections', { headers: authHeader() });
        const connections = response.data || {};
        setCrmConnections(connections);

        const connectedCrms = Object.keys(connections).filter(key => connections[key]?.connected);
        if (connectedCrms.length > 0) {
          await autoSyncAllCrms(connectedCrms);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          console.log('CRM connections endpoint not yet implemented');
          setCrmConnections({});
        } else {
          console.error('Failed to load CRM connections:', error);
        }
      }
    };

    if (currentUser) {
      loadAndSyncCrmConnections();
    }
  }, [currentUser]);

  const autoSyncAllCrms = async (crmKeys) => {
    try {
      setSyncing(true);
      let totalClients = 0;

      for (const crmKey of crmKeys) {
        try {
          const response = await axios.post(
            `/api/clients/me/crm-connections/${crmKey}/sync`,
            {},
            { headers: authHeader() }
          );
          const syncedData = response.data;
          if (syncedData?.clients) {
            sessionStorage.setItem(`crm_${crmKey}_clients`, JSON.stringify(syncedData.clients));
            totalClients += syncedData.clients.length;
          }
        } catch (error) {
          console.log(`Auto-sync failed for ${crmKey}:`, error.message);
        }
      }

      setLastSyncTime(new Date());
      if (totalClients > 0) {
        console.log(`Auto-synced ${totalClients} clients from ${crmKeys.length} CRM(s)`);
      }
    } catch (error) {
      console.error('Auto-sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleConnect = async (crmKey) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/clients/me/crm-connections/${crmKey}/oauth/initiate`,
        { returnUrl: window.location.href },
        { headers: authHeader() }
      );

      if (response.data?.authUrl) {
        sessionStorage.setItem('crm_oauth_pending', crmKey);
        window.location.href = response.data.authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      console.error('Failed to initiate OAuth for CRM:', error);
      if (error.response?.status === 404) {
        message.error(`OAuth integration for ${CRM_PLATFORMS.find(p => p.key === crmKey)?.name} is not yet configured.`);
      } else {
        message.error('Failed to connect CRM. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (crmKey) => {
    try {
      setLoading(true);
      setCrmConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections[crmKey];
        return newConnections;
      });
      message.success(`${CRM_PLATFORMS.find(p => p.key === crmKey)?.name} disconnected successfully!`);
    } catch (error) {
      console.error('Failed to disconnect CRM:', error);
      message.error('Failed to disconnect CRM. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (crmKey) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/clients/me/crm-connections/${crmKey}/sync`,
        {},
        { headers: authHeader() }
      );

      if (response.data?.clients) {
        sessionStorage.setItem(`crm_${crmKey}_clients`, JSON.stringify(response.data.clients));
        setLastSyncTime(new Date());
        message.success(`Synced ${response.data.clients.length} clients from ${CRM_PLATFORMS.find(p => p.key === crmKey)?.name}!`);
      } else {
        message.success(`Synced data from ${CRM_PLATFORMS.find(p => p.key === crmKey)?.name}!`);
      }
    } catch (error) {
      console.error('Failed to sync CRM:', error);
      if (error.response?.status === 404) {
        message.error(`Sync endpoint for ${CRM_PLATFORMS.find(p => p.key === crmKey)?.name} is not configured.`);
      } else if (error.response?.status === 401) {
        message.error('CRM connection expired. Please reconnect.');
      } else {
        message.error('Failed to sync CRM data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mcp-header-strip" style={{ marginBottom: 24 }}>
        <div>
          <div className="mcp-eyebrow">MCP · CRM</div>
          <h1 className="mcp-header-title">CRM Integrations</h1>
          <p className="mcp-header-subtitle">
            Connect your CRM platforms to sync client lists and enable targeted campaigns.
          </p>
        </div>
        <div className="mcp-header-stats">
          <div className="mcp-stat">
            <div className="mcp-stat-label">Connected</div>
            <div className="mcp-stat-value">
              {Object.values(crmConnections).filter(c => c?.connected).length}
            </div>
          </div>
          <div className="mcp-stat">
            <div className="mcp-stat-label">Available</div>
            <div className="mcp-stat-value">{CRM_PLATFORMS.length}</div>
          </div>
        </div>
      </div>

      {syncing && (
        <div className="mb-4 flex items-center gap-2 rounded-2xl border border-paper-300 bg-paper p-3 text-sm font-semibold text-ink-100">
          <Spin size="small" />
          Auto-syncing CRM data…
        </div>
      )}

      {lastSyncTime && (
        <div className="mb-4 rounded-2xl border border-paper-300 bg-paper p-3 text-sm text-ink-200">
          <span className="font-semibold text-sage">Last synced:</span> {lastSyncTime.toLocaleString()}
          <p className="mt-1 text-xs">CRM data is automatically synced on login and available for your campaigns.</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
        {CRM_PLATFORMS.map((platform) => {
          const connection = crmConnections[platform.key];
          const isConnected = !!connection?.connected;

          return (
            <CrmCard
              key={platform.key}
              platform={platform}
              isConnected={isConnected}
              connection={connection}
              onConnect={() => handleConnect(platform.key)}
              onSync={() => handleSync(platform.key)}
              onDisconnect={() => handleDisconnect(platform.key)}
              loading={loading}
            />
          );
        })}
      </div>
    </div>
  );
}
